/**
 * Fan-out fetch of calibration entries across every org project (RAISE-16869 D4).
 *
 * GET /api/v1/calibration requires `project_id` (no org-wide listing), so the
 * Cost page's org-wide view fans out one request per project via useQueries.
 * Per-project failures are tolerated: successful pages are merged and
 * `failedProjects` is surfaced in the result; only an all-projects failure
 * produces `error`.
 */

import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";
import { useProjects } from "@/hooks/useProjects";
import type { CalibrationEntry } from "@/lib/cost/costPerStoryPoint";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CalibrationEntryRaw {
  entry_id: string;
  story: string;
  sp: number | null;
  created_at: string;
}

interface CalibrationListResponse {
  project_id: string;
  entries: CalibrationEntryRaw[];
  total: number;
}

export interface UseCalibrationEntriesResult {
  entries: CalibrationEntry[];
  loading: boolean;
  error: string | null;
  failedProjects: number;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCalibrationEntries(): UseCalibrationEntriesResult {
  const { projects, loading: projectsLoading, error: projectsError } = useProjects();

  // Stabilize the slug list on content so useQueries doesn't churn on every
  // render (same pattern as useStoryCosts' stableRefs).
  const slugs = useMemo(() => projects.map((p) => p.slug), [projects]);
  const slugsKey = slugs.join(",");
  const stableSlugs = useMemo(() => slugs, [slugsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const queryResults = useQueries({
    queries: stableSlugs.map((slug) => ({
      queryKey: queryKeys.metrics.calibration(slug),
      queryFn: (): Promise<CalibrationListResponse> =>
        api<CalibrationListResponse>(
          `/api/v1/calibration?project_id=${encodeURIComponent(slug)}`,
        ),
      staleTime: 300_000,
      retry: (failureCount: number) => failureCount < 2,
    })),
  });

  const loading = projectsLoading || (stableSlugs.length > 0 && queryResults.some((q) => q.isLoading));

  const entries: CalibrationEntry[] = [];
  let failedProjects = 0;
  queryResults.forEach((q, i) => {
    const slug = stableSlugs[i];
    if (q.isSuccess && q.data) {
      for (const raw of q.data.entries) {
        entries.push({
          entryId: raw.entry_id,
          story: raw.story,
          sp: raw.sp,
          createdAt: raw.created_at,
          projectSlug: slug,
        });
      }
    } else if (q.isError) {
      failedProjects++;
    }
  });

  const allFailed = stableSlugs.length > 0 && failedProjects === stableSlugs.length;
  const error = projectsError
    ? projectsError
    : allFailed
      ? "Failed to load calibration data"
      : null;

  return { entries, loading, error, failedProjects };
}
